import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, isValid } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { userService, calculateUserCO2Offset } from "@/api/userService";
import { Clock, Route, Bike, Leaf } from "lucide-react";

interface UserRideHistoryProps {
  userId: string;
}

const UserRideHistory: React.FC<UserRideHistoryProps> = ({ userId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  
  const { data, isLoading, error } = useQuery({
    queryKey: ["userRides", userId, currentPage, pageSize],
    queryFn: () => userService.getUserRides(userId, currentPage, pageSize),
    placeholderData: (previousData) => previousData,
  });
  
  if (error) {
    console.error(`Error fetching rides for user ${userId}:`, error);
    return (
      <div className="flex flex-col items-center justify-center rounded-md border py-8 text-center">
        <div className="rounded-full bg-red-100 p-3">
          <span className="text-red-600">!</span>
        </div>
        <h3 className="mt-3 text-sm font-medium">Error Loading Ride History</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          We encountered an error loading this user's ride history.
        </p>
        <Button variant="link" className="mt-2" onClick={() => window.location.reload()}>
          Try Again
        </Button>
      </div>
    );
  }
  
  if (isLoading) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bike</TableHead>
              <TableHead>Date & Time</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Route</TableHead>
              <TableHead>Distance</TableHead>
              <TableHead>CO2 Saved</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: pageSize }).map((_, index) => (
              <TableRow key={index}>
                <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[180px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }
  
  const rides = data?.rides || [];
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  
  if (rides.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-md border py-8 text-center">
        <div className="rounded-full bg-muted p-3">
          <Clock className="h-6 w-6 text-muted-foreground" />
        </div>
        <h3 className="mt-3 text-sm font-medium">No Ride History</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          This user doesn't have any recorded rides yet.
        </p>
      </div>
    );
  }
  
  const formatSafeDate = (dateString: Date | string) => {
    const date = new Date(dateString);
    return isValid(date) && date.getFullYear() > 1970 
      ? date 
      : new Date();
  };
  
  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Bike</TableHead>
            <TableHead>Date & Time</TableHead>
            <TableHead>Duration</TableHead>
            <TableHead>Route</TableHead>
            <TableHead>Distance</TableHead>
            <TableHead>CO2 Saved</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rides.map((ride) => (
            <TableRow key={ride.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Bike className="h-4 w-4 text-muted-foreground" />
                  <span>{ride.bikeName || ride.serialNumber || `Bike #${ride.bikeId}`}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="text-sm">
                  {format(formatSafeDate(ride.startTime), "dd MMM yyyy")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {format(formatSafeDate(ride.startTime), "HH:mm")} - {format(formatSafeDate(ride.endTime), "HH:mm")}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{ride.duration === null ? 'N/A' : `${ride.duration} min`}</span>
                </div>
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  <Route className="h-3.5 w-3.5 text-muted-foreground" />
                  <span>{ride.startLocation} → {ride.endLocation}</span>
                </div>
              </TableCell>
              <TableCell>{ride.distance.toFixed(2)} km</TableCell>
              <TableCell>
                {ride.countTowardsOffset !== false && (
                  <div className="flex items-center gap-1 text-green-600">
                    <Leaf className="h-3.5 w-3.5" />
                    <span>{calculateUserCO2Offset(ride.distance)} kg</span>
                  </div>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      
      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t px-4 py-2">
          <div className="text-sm text-muted-foreground">
            Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{" "}
            <span className="font-medium">
              {Math.min(currentPage * pageSize, totalItems)}
            </span>{" "}
            of <span className="font-medium">{totalItems}</span> rides
          </div>
          <div className="space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserRideHistory;
